import LoaderWrapper from 'Common/LoaderWrapper/LoaderWrapper';
import Show from 'Components/Show/Show'
import api from 'http';
import React, { useEffect, useState } from 'react'
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';
import AddNewCardForm from './AddNewCardForm';
import { FiAlertCircle, FiChevronDown } from 'react-icons/fi';
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { Tooltip } from 'react-tooltip';

const CreditCardPaymentMethod = ({ publishableKey, paymentModel, handlePaymentMethod, handleCreditCardChange, cardFormRef, validationModel }) => {
    const [creditCardLoader, setCreditCardLoader] = useState(false);
    const [creditCards, setCreditCards] = useState([]);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (paymentModel.stripeSettingsId && paymentModel.patientId && paymentModel.patientPaymentType === 'CC_PMT') {
            (async () => {
                try {
                    setCreditCardLoader(true)
                    const resp = await api.get(`${process.env.REACT_APP_TC_PMS}/api/StripeConfiguration/FindStripePatientPaymentMethods/${paymentModel.patientId}/${paymentModel.stripeSettingsId}`);
                    if (resp.data.dataModel.patientCardInfo.length > 0) {
                        setCreditCards(resp.data.dataModel.patientCardInfo || []);
                        if (resp.data.dataModel.patientCardInfo.some(card => card.isDefault)) {
                            handleCreditCardChange({ target: { name: 'paymentMethodId', value: resp.data.dataModel.patientCardInfo.find(card => card.isDefault).id } }, resp.data.dataModel.patientCardInfo);
                        }
                    }
                    else {
                        setCreditCards([]);
                    }
                } catch (error) {
                    ErrorHandlingMessage(error)
                } finally {
                    setCreditCardLoader(false)
                }
            })();
        }

    }, [paymentModel.stripeSettingsId, paymentModel.patientId, paymentModel.patientPaymentType, handleCreditCardChange])

    const getDisplayText = () => {
        if (!paymentModel.paymentMethodId || paymentModel.paymentMethodId === "") {
            return 'Please Select';
        }
        if (paymentModel.paymentMethodId === "no-payment-id") {
            return '+ Add New Card';
        }
        const selectedCard = creditCards.find(card => card.id === paymentModel.paymentMethodId);
        if (selectedCard) {
            return `${selectedCard.cardType.toUpperCase()} •••• ${selectedCard.cardNumber.slice(-4)}`;
        }
        return 'Please Select';
    }

    const getSelectedCard = () => {
        if (!paymentModel.paymentMethodId || paymentModel.paymentMethodId === "" || paymentModel.paymentMethodId === "no-payment-id") {
            return null;
        }
        return creditCards.find(card => card.id === paymentModel.paymentMethodId);
    }

    const handleSelect = (value) => {
        const event = {
            target: {
                name: 'paymentMethodId',
                value: value
            }
        };
        handleCreditCardChange(event, creditCards);
        setIsOpen(false);
    }

    return (
        <div>
            <Tooltip id="card-item-tooltip" style={{ zIndex: 2, maxWidth: 220 }} />
            <div className="flex items-center">
                <input type="radio" id="CC_PMT" name="patientPaymentType" value="CC_PMT" checked={paymentModel.patientPaymentType === 'CC_PMT'} onChange={handlePaymentMethod} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                <label htmlFor="CC_PMT" className="ml-3 text-sm text-slate-700">Pay via Credit Card</label>
            </div>
            <Show when={paymentModel.patientPaymentType === 'CC_PMT'}>
                <div className="mt-2 space-y-3 ml-7">
                    <LoaderWrapper loading={creditCardLoader} className="h-[38px]">
                        <RadixDropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
                            <RadixDropdownMenu.Trigger disabled={!paymentModel.stripeSettingsId} asChild>
                                <button
                                    disabled={creditCardLoader}
                                    className={`customInputClass border h-9 w-full text-left flex items-center justify-between pr-2 cursor-pointer${isOpen ? 'border border-primary rounded' : ''} ${creditCardLoader ? 'cursor-not-allowed opacity-50' : ''}`}
                                >
                                    <div className="flex items-center text-xs">
                                        <span className={paymentModel.paymentMethodId && paymentModel.paymentMethodId !== "" ? 'text-gray-900' : 'text-gray-400'}>
                                            {getDisplayText()}
                                        </span>
                                        <Show when={getSelectedCard()?.isDefault}>
                                            <span data-tooltip-id="card-item-tooltip" data-tooltip-content="This is your default payment method" className="px-2 py-1 ml-3 font-medium ">
                                                Default
                                            </span>
                                        </Show>
                                    </div>
                                    <FiChevronDown size={16} className={`text-gray-900 transition-transform`} />
                                </button>
                            </RadixDropdownMenu.Trigger>

                            <RadixDropdownMenu.Portal>
                                <RadixDropdownMenu.Content
                                    className="min-w-[var(--radix-dropdown-menu-trigger-width)] z-50 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-auto"
                                    sideOffset={5}
                                    align="start"
                                >
                                    <RadixDropdownMenu.Item
                                        key="Please Select"
                                        onSelect={() => handleSelect("")}
                                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-200 text-gray-900`}
                                    >
                                        Please Select
                                    </RadixDropdownMenu.Item>
                                    {creditCards.map((card) => (
                                        <RadixDropdownMenu.Item
                                            key={card.id}
                                            onSelect={() => handleSelect(card.id)}
                                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-200 ${paymentModel.paymentMethodId === card.id ? 'bg-primaryColor/10 text-primaryColor font-medium' : 'text-gray-900'
                                                }`}
                                        >
                                            <div className="flex items-center text-xs">
                                                <span>{card.cardType.toUpperCase()} •••• {card.cardNumber.slice(-4)}</span>
                                                <Show when={card.isDefault}>
                                                    <span data-tooltip-id="card-item-tooltip" data-tooltip-content="This is your default payment method" className="px-2 py-1 ml-3 font-medium ">
                                                        Default
                                                    </span>
                                                </Show>
                                            </div>
                                        </RadixDropdownMenu.Item>
                                    ))}
                                    <RadixDropdownMenu.Item
                                        key="no-payment-id"
                                        onSelect={() => handleSelect("no-payment-id")}
                                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${paymentModel.paymentMethodId === "no-payment-id" ? 'bg-primaryColor/10 text-primaryColor font-medium' : 'text-gray-900'
                                            }`}
                                    >
                                        + Add New Card
                                    </RadixDropdownMenu.Item>
                                </RadixDropdownMenu.Content>
                            </RadixDropdownMenu.Portal>
                        </RadixDropdownMenu.Root>

                        {validationModel.last4DigitsError && (
                            <div className="flex items-center gap-2 p-3 mt-2 border border-red-200 rounded-lg bg-red-50">
                                <FiAlertCircle className="flex-shrink-0 w-4 h-4 text-red-600" />
                                <span className="text-sm text-red-700">
                                    {validationModel.last4DigitsError}
                                </span>
                            </div>
                        )}
                    </LoaderWrapper>
                    <Show when={paymentModel.cardPaymentMethod === "NEW"}>
                        <AddNewCardForm publishableKey={publishableKey} ref={cardFormRef} />
                    </Show>
                </div>
            </Show>

        </div>
    )
}

export default CreditCardPaymentMethod